import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, type AuthUser } from "@/lib/api";

function cleanSpaces(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

function toTitleCase(value: string) {
  return cleanSpaces(value)
    .toLowerCase()
    .split(" ")
    .map((word) => {
      if (!word) return word;
      // Keep short particles lowercase in Spanish names when mid-name
      if (["de", "del", "la", "las", "los", "y", "e"].includes(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ")
    .replace(/^(de|del|la|las|los|y|e)\b/i, (m) => m.charAt(0).toUpperCase() + m.slice(1).toLowerCase());
}

interface Props {
  student: Pick<AuthUser, "id" | "full_name" | "company" | "position" | "phone" | "email">;
  onClose: () => void;
  onSaved: (updated: AuthUser) => void;
}

export default function EditStudentProfileModal({ student, onClose, onSaved }: Props) {
  const [form, setForm] = useState({
    full_name: student.full_name || "",
    company: student.company || "",
    position: student.position || "",
    phone: student.phone || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      full_name: student.full_name || "",
      company: student.company || "",
      position: student.position || "",
      phone: student.phone || "",
    });
  }, [student]);

  const applyName = (transform: (v: string) => string) => {
    setForm((prev) => ({ ...prev, full_name: transform(prev.full_name) }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const full_name = cleanSpaces(form.full_name);
    if (!full_name) {
      toast.error("El nombre no puede quedar vacío.");
      return;
    }
    setSaving(true);
    try {
      const updated = await api.updateUser(student.id, {
        full_name,
        company: cleanSpaces(form.company),
        position: cleanSpaces(form.position),
        phone: cleanSpaces(form.phone),
      });
      toast.success("Datos del participante actualizados. Los certificados usarán este nombre.");
      onSaved(updated);
      onClose();
    } catch (err: any) {
      toast.error(err.message || "No se pudo guardar el nombre");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-[var(--color-bg-card)] rounded-2xl w-full max-w-lg border border-[var(--color-border)] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-[var(--color-border)]">
          <div>
            <h2 className="text-lg font-bold text-[var(--color-text-primary)]">Corregir datos del participante</h2>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              Este nombre aparece en el certificado PDF y en la validación pública.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--color-bg-tertiary)] rounded-lg cursor-pointer">
            <i className="ri-close-line text-xl text-[var(--color-text-muted)]"></i>
          </button>
        </div>

        <form onSubmit={handleSave} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
              Nombre completo (certificado) *
            </label>
            <input
              className="input-field text-lg font-semibold"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              required
              placeholder="Ej. MARÍA DEL CARMEN PUEBA"
              autoFocus
            />
            <div className="flex flex-wrap gap-2 mt-2">
              <button
                type="button"
                onClick={() => applyName((v) => cleanSpaces(v).toUpperCase())}
                className="btn-secondary text-xs py-1.5 px-3 cursor-pointer"
              >
                MAYÚSCULAS
              </button>
              <button
                type="button"
                onClick={() => applyName(toTitleCase)}
                className="btn-secondary text-xs py-1.5 px-3 cursor-pointer"
              >
                Tipo Título
              </button>
              <button
                type="button"
                onClick={() => applyName(cleanSpaces)}
                className="btn-secondary text-xs py-1.5 px-3 cursor-pointer"
              >
                Limpiar espacios
              </button>
            </div>
            <p className="text-xs text-[var(--color-text-muted)] mt-2">
              Vista previa certificado:{" "}
              <span className="font-semibold text-[var(--color-text-primary)]">
                {cleanSpaces(form.full_name) || "—"}
              </span>
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Empresa</label>
              <input
                className="input-field"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder="Empresa"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Puesto</label>
              <input
                className="input-field"
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                placeholder="Puesto / cargo"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Teléfono</label>
            <input
              className="input-field"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="Teléfono"
            />
          </div>

          <div className="rounded-xl bg-[var(--color-bg-tertiary)] border border-[var(--color-border)] p-3 text-xs text-[var(--color-text-muted)]">
            Correo (no editable aquí): <span className="text-[var(--color-text-secondary)]">{student.email}</span>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary cursor-pointer" disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary cursor-pointer disabled:opacity-50" disabled={saving}>
              {saving ? (
                <><i className="ri-loader-4-line animate-spin mr-1"></i> Guardando...</>
              ) : (
                <><i className="ri-save-line mr-1"></i> Guardar para certificados</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
